import React, { useEffect, useRef, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider,
  Typography, IconButton, Container, Alert, Box, Card, Stack, TextField, MenuItem,
  Avatar, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  CircularProgress, Chip, FormControl, InputLabel, Select, Tooltip, Popover, List,
  ListItem, ListItemText, ListItemIcon, Switch, useTheme, useMediaQuery, Grid,
  CardContent, CardActions, Collapse, IconButton as MuiIconButton,
} from "@mui/material";
import ClassRoomForm from "../view-form/ClassRoomForm";
import authService from "../Auth/AuthService";
import { getAcademicYear } from "../utils/academicYear";
import {
  Add, AddCard, CurrencyRupee, Edit, Refresh,
  SupervisedUserCircle, TableViewSharp, TextFields, VerifiedUser,
  Send as SendIcon,
  CheckBox as CheckBoxIcon,
  Message as MessageIcon,
  Description as DescriptionIcon,
  ViewColumn as ViewColumnIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  CalendarToday as CalendarTodayIcon,
  School as SchoolIcon,
  AttachMoney as AttachMoneyIcon,
  Book as BookIcon,
  Badge as BadgeIconNew,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Wallet,
  ReceiptLong,
  FactCheck,
} from "@mui/icons-material";
import ModalView from "../model/ModelView";
import MarksheetForm from "../marksheet/MarksheetForm";
import { useNavigate } from "react-router-dom";
import FeesentryForm from "../view-form/FeesentryForm";
import FeesentryView from "../view-model/FeesentryView";
import BadgeIcon from "@mui/icons-material/Badge";
import EntryCardForm from "../view-form/EntryCardForm";

const ClassRoomPage = ({ user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [classRooms, setClassRooms] = useState([]);
  const [classRoom, setClassRoom] = useState({});
  const [year, setYear] = useState("");
  const [editingClassRoom, setEditingClassRoom] = useState(null);
  const hasFetched = useRef(false);
  const [openForm, setOpenForm] = useState(false);
  const [feesForm, setFeesForm] = useState(false);
  const [feesEntry, setFeesEntry] = useState(null);
  const navigate = useNavigate();
  const [classRoomFilter, setClassRoomFilter] = useState(null);
  const [feesFormView, setFeesFormView] = useState(false);
  const [selectedStandard, setSelectedStandard] = useState("");
  const [standards, setStandards] = useState();
  const [school, setSchool] = useState({});
  const [openEntry, setOpenEntry] = useState(false);
  const [markEntry, setMarkEntry] = useState({});
  const [openEntryCard, setOpenEntryCard] = useState(false);
  const [entryCard, setEntryCard] = useState({});

  // State for select all and send message
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [openMessageDialog, setOpenMessageDialog] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(1);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [messageSuccess, setMessageSuccess] = useState("");

  const roles = user?.roles;
  const DOC_URL = authService.DOC_URL

  // Expand/collapse for mobile card view
  const [expandedCards, setExpandedCards] = useState({});

  // Column customization state - All fields included
  const [columnVisibility, setColumnVisibility] = useState({
    select: true,
    id: true,
    photo: !isMobile,
    studentName: true,
    fatherName: false,
    motherName: false,
    standard: true,
    section: false,
    medium: false,
    academicYear: !isMobile,
    rollNumber: !isMobile,
    patternType: false,
    studentType: false,
    dob: !isMobile,
    mobile: !isMobile,
    address: false,
    promotionDate: false,
    admissionDate: false,
    active: !isMobile,
    annualfee: !isMobile,
    // dueAmount: !isMobile,
    fees: true,
    attendance: true,
    marks: true,
    actions: true,
  });

  const [columnOrder, setColumnOrder] = useState([
    'select',
    'id',
    'photo',
    'studentName',
    'fatherName',
    'motherName',
    'standard',
    'section',
    'medium',
    'academicYear',
    'rollNumber',
    'patternType',
    'studentType',
    'dob',
    'mobile',
    'address',
    'promotionDate',
    'admissionDate',
    'active',
    'annualfee',
    // 'dueAmount',
    'fees',
    'attendance',
    'marks',
    'actions'
  ]);

  // Column customization popover state
  const [anchorEl, setAnchorEl] = useState(null);
  const openColumnPopover = Boolean(anchorEl);

  // Message Templates
  const messageTemplates = {
    1: "Dear Parent, your child's school fee is pending. Kindly make the payment at your earliest convenience. If you have already paid, please ignore this message. Thank you.",
    2: "Dear Parent, your child's school fee is pending. Kindly complete the payment before the due date to avoid late charges. If you have already paid, please ignore this message.",
    3: "Dear Parent, this is a reminder regarding the pending school fee. Kindly clear the outstanding amount at your earliest convenience. If you have already paid, please ignore this message.",
    4: "Dear Parent, your child's school fee is due. We kindly request you to make the payment at the earliest. If you have already completed the payment, please ignore this reminder."
  };

  // Template options for dropdown
  const templateOptions = [
    { value: 1, label: "Template 1 - Standard Reminder" },
    { value: 2, label: "Template 2 - With Due Date Warning" },
    { value: 3, label: "Template 3 - Polite Reminder" },
    { value: 4, label: "Template 4 - Urgent Request" },
  ];

  // Column configuration with all fields
  const columnConfig = {
    select: { label: "Select", icon: <CheckBoxIcon /> },
    id: { label: "Class ID", icon: <BadgeIconNew /> },
    photo: { label: "Photo", icon: <Avatar /> },
    studentName: { label: "Student Name", icon: <PersonIcon /> },
    fatherName: { label: "Father Name", icon: <PersonIcon /> },
    motherName: { label: "Mother Name", icon: <PersonIcon /> },
    standard: { label: "Standard", icon: <SchoolIcon /> },
    section: { label: "Section", icon: <TextFields /> },
    medium: { label: "Medium", icon: <BookIcon /> },
    academicYear: { label: "Academic Year", icon: <CalendarTodayIcon /> },
    rollNumber: { label: "Roll Number", icon: <BadgeIconNew /> },
    patternType: { label: "Pattern", icon: <BookIcon /> },
    studentType: { label: "Student Type", icon: <SchoolIcon /> },
    dob: { label: "Date of Birth", icon: <CalendarTodayIcon /> },
    mobile: { label: "Mobile", icon: <PhoneIcon /> },
    address: { label: "Address", icon: <HomeIcon /> },
    promotionDate: { label: "Promotion Date", icon: <CalendarTodayIcon /> },
    admissionDate: { label: "Admission Date", icon: <CalendarTodayIcon /> },
    active: { label: "Status", icon: <VerifiedUser /> },
    annualfee: { label: "Annual Fee", icon: <AttachMoneyIcon /> },
    // dueAmount: { label: "Due Amount", icon: <MoneyOffIcon /> },
    fees: { label: "Fees", icon: <CurrencyRupee /> },
    attendance: { label: "Attendance", icon: <VerifiedUser /> },
    marks: { label: "Mark Entry", icon: <TableViewSharp /> },
    actions: { label: "Actions", icon: <Edit /> },
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setYear(getAcademicYear(new Date()))
    handleRefreshSection();
    sessionStorage.setItem("hide", true);

    // Load saved column preferences from localStorage
    loadColumnPreferences();
  }, []);

  // Load column preferences from localStorage
  const loadColumnPreferences = () => {
    try {
      const savedVisibility = localStorage.getItem('classroom_columnVisibility');
      const savedOrder = localStorage.getItem('classroom_columnOrder');

      if (savedVisibility) {
        setColumnVisibility(JSON.parse(savedVisibility));
      }
      if (savedOrder) {
        setColumnOrder(JSON.parse(savedOrder));
      }
    } catch (error) {
      console.error('Error loading column preferences:', error);
    }
  };

  // Save column preferences to localStorage
  const saveColumnPreferences = (visibility, order) => {
    try {
      localStorage.setItem('classroom_columnVisibility', JSON.stringify(visibility));
      localStorage.setItem('classroom_columnOrder', JSON.stringify(order));
    } catch (error) {
      console.error('Error saving column preferences:', error);
    }
  };

  // Handle column visibility toggle
  const handleColumnToggle = (columnKey) => {
    // Prevent hiding select column (always show for selection)
    if (columnKey === 'select') return;

    setColumnVisibility(prev => {
      const newVisibility = {
        ...prev,
        [columnKey]: !prev[columnKey]
      };
      saveColumnPreferences(newVisibility, columnOrder);
      return newVisibility;
    });
  };

  // Reset columns to default
  const handleResetColumns = () => {
    const defaultVisibility = {
      select: true,
      id: true,
      photo: !isMobile,
      studentName: true,
      fatherName: !isMobile,
      motherName: false,
      standard: true,
      section: !isMobile,
      medium: false,
      academicYear: !isMobile,
      rollNumber: !isMobile,
      patternType: false,
      studentType: false,
      dob: !isMobile,
      mobile: !isMobile,
      address: false,
      promotionDate: false,
      admissionDate: false,
      active: !isMobile,
      annualfee: !isMobile,
      // dueAmount: !isMobile,
      fees: true,
      attendance: true,
      marks: true,
      actions: true,
    };
    const defaultOrder = [
      'select',
      'id',
      'photo',
      'studentName',
      'fatherName',
      'motherName',
      'standard',
      'section',
      'medium',
      'academicYear',
      'rollNumber',
      'patternType',
      'studentType',
      'dob',
      'mobile',
      'address',
      'promotionDate',
      'admissionDate',
      'active',
      'annualfee',
      // 'dueAmount',
      'fees',
      'attendance',
      'marks',
      'actions'
    ];
    setColumnVisibility(defaultVisibility);
    setColumnOrder(defaultOrder);
    saveColumnPreferences(defaultVisibility, defaultOrder);
  };

  // Handle column popover open
  const handleColumnPopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle column popover close
  const handleColumnPopoverClose = () => {
    setAnchorEl(null);
  };

  // Get visible columns in order
  const getVisibleColumns = () => {
    return columnOrder.filter(col => columnVisibility[col] !== false);
  };

  const handleRefreshSection = () => {
    loadClassRooms();
    loadSchool();
    setSelectedStudents([]);
    setSelectAll(false);
  }

  const loadClassRooms = async () => {
    try {
      const url = authService.pathUrl('GET', '/classroom/academic/school/' + `${user?.mas_code}` + '/' + getAcademicYear(new Date()));
      const res = await authService.getAuth(url);
      setClassRoomFilter(res);
      setClassRooms(res);
      setStandards(Array.from(new Set(res.map(r => r.standard))));
    } catch (error) {
      console.error("Error fetching schools:", error);
    }
  };

  const loadSchool = async () => {
    try {
      if (user?.mas_id !== undefined && user?.mas_id !== 'undefined') {
        const url = authService.pathUrl("GET", "/schools/email");
        const schoolsData = await authService.getAuth(url);
        const updatedForm = {
          ...schoolsData,
          logoUrl: DOC_URL + schoolsData.logoUrl
        };
        setSchool(updatedForm);
      }
    } catch (error) {
      console.error("Error fetching schools:", error);
    }
  }

  const handleAdd = async (data) => {
    if (editingClassRoom) {
      const url = authService.pathUrl('PUT', '/classroom/promote/' + editingClassRoom.classId)
      await authService.putAuth(url, data);
      setEditingClassRoom(null);
    } else {
      const url = authService.pathUrl('POST', '/classroom/promote')
      await authService.postAuth(url, data);
    }
    setOpenForm(false);
    loadClassRooms();
  };

  const handleEdit = (classRoom) => {
    setOpenForm(true);
    setEditingClassRoom(classRoom);
  };

  const handlePromo = (classRoom) => {
    setOpenForm(true);
    const data = {
      studentId: classRoom?.studentId,
      classRoomId: classRoom?.classId,
      academicYear: year,
    }
    setClassRoom(data);
  };

  const handleClose = () => {
    setOpenForm(false);
    setEditingClassRoom(null);
    setFeesForm(false);
    setFeesFormView(false);
    setOpenEntry(false);
    setOpenEntryCard(false);
  };

  const handleCloseFee = (data) => {
    if (data) {
      setFeesFormView(false)
      setFeesForm(true);
    }
  };

  const handleAttend = async (classRoom, attend) => {
    const data = {
      id: classRoom?.attendance?.id,
      studentId: classRoom?.studentId,
      teacherId: user?.mas_id,
      attendance: attend,
      createdBy: user?.sub,
    }
    if (classRoom?.attendance?.id) {
      const url = authService.pathUrl('PUT', '/student-attendance/' + classRoom?.attendance?.id)
      await authService.putAuth(url, data);
      setEditingClassRoom(null);
    } else {
      const url = authService.pathUrl('POST', '/student-attendance')
      await authService.postAuth(url, data);
    }
    loadClassRooms();
  }

  const handleMarkentry = async (data) => {
    setOpenEntry(true);
    setMarkEntry(data);
  };

  const handleChange = (e) => {
    const standard = e.target.value;
    setSelectedStandard(standard);
    const filteredRooms = classRoomFilter.filter(
      (room) => String(room.standard) === String(standard)
    );
    setClassRooms(filteredRooms);
    setSelectedStudents([]);
    setSelectAll(false);
  };

  // Handle select all
  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allIds = classRooms.map(cr => cr.classId);
      setSelectedStudents(allIds);
    } else {
      setSelectedStudents([]);
    }
  };

  // Handle individual student selection
  const handleStudentSelect = (classId) => {
    setSelectedStudents(prev => {
      const newSelection = prev.includes(classId)
        ? prev.filter(id => id !== classId)
        : [...prev, classId];

      setSelectAll(newSelection.length === classRooms.length && classRooms.length > 0);
      return newSelection;
    });
  };

  // Open message dialog
  const handleOpenMessageDialog = () => {
    if (selectedStudents.length === 0) {
      setMessageError("Please select at least one student");
      setTimeout(() => setMessageError(""), 3000);
      return;
    }
    setSelectedTemplate(1);
    setMessageText(messageTemplates[1]);
    setOpenMessageDialog(true);
    setMessageError("");
    setMessageSuccess("");
  };

  // Handle template change from dropdown
  const handleTemplateChange = (event) => {
    const templateNumber = parseInt(event.target.value);
    setSelectedTemplate(templateNumber);
    setMessageText(messageTemplates[templateNumber]);
  };

  // Handle manual message edit
  const handleMessageEdit = (event) => {
    setMessageText(event.target.value);
  };

  // Send message API call
  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      setMessageError("Please enter a message");
      return;
    }

    setSendingMessage(true);
    setMessageError("");
    setMessageSuccess("");

    try {
      const selectedStudentDetails = classRooms
        .filter(cr => selectedStudents.includes(cr.classId))
        .map(cr => ({
          studentId: cr.studentId,
          studentName: cr.studentName,
          mobile: cr.mobile,
          classId: cr.classId,
          standard: cr.standard,
          section: cr.section,
        }));

      const payload = {
        studentIds: selectedStudents,
        message: messageText,
        senderId: user?.mas_id,
        schoolCode: user?.mas_code,
        subject: "Pending Fees Notification",
        recipientType: "STUDENT_PARENT",
        studentDetails: selectedStudentDetails,
        templateId: selectedTemplate,
      };

      const url = authService.pathUrl('POST', '/fees/send-pending-fees-message');
      const response = await authService.postAuth(url, payload);

      if (response) {
        setMessageSuccess(`Message sent successfully to ${selectedStudents.length} student(s)`);
        setSelectedStudents([]);
        setSelectAll(false);
        setTimeout(() => {
          setOpenMessageDialog(false);
          setMessageSuccess("");
        }, 3000);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessageError("Failed to send message. Please try again.");
    } finally {
      setSendingMessage(false);
    }
  };

  if (classRooms?.length === 0) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 5 }}>
          {"No records found"}
        </Alert>
      </Container>
    );
  }

  function toTitleCase(str) {
    if (!str) return 'N/A';
    return str
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  const handleOpenIDcard = () => {
    sessionStorage.setItem("hide", false);
    const selectedStudents = classRooms.map(data => ({
      id: data.classId,
      name: toTitleCase(data.studentName),
      father: toTitleCase(data.fatherName),
      standard: data.standard,
      section: data.section,
      dob: data.dob,
      academicYear: data.academicYear,
      photo: DOC_URL + data.photo,
      mobile: data.mobile,
      address: toTitleCase(data.address),
      logo: school.logoUrl,
      school_name: toTitleCase(school.schoolName),
      school_address: toTitleCase([school.addressDTO?.addressLine1, school.addressDTO?.city, school.addressDTO?.pincode, school.addressDTO?.state].filter(Boolean).join(", ")),
      school_phone: school.contactPhone,
    }));
    navigate('/idcard', {
      state: {
        students: selectedStudents
      }
    })
  };

  const handleOpenEntrycard = () => {
    const selectedStudents = classRooms.map(data => ({
      id: data.classId,
      name: toTitleCase(data.studentName),
      father: toTitleCase(data.fatherName),
      mother: toTitleCase(data.motherName),
      standard: data.standard,
      section: data.section,
      academicYear: data.academicYear,
      rollNumber: data.rollNumber,
      logo: school.logoUrl,
      school_name: toTitleCase(school.schoolName),
      school_address: toTitleCase([school.addressDTO?.addressLine1, school.addressDTO?.city, school.addressDTO?.pincode, school.addressDTO?.state].filter(Boolean).join(", ")),
      school_phone: school.contactPhone,
    }));
    setEntryCard(selectedStudents);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Toggle card expand
  const toggleCardExpand = (classId) => {
    setExpandedCards(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  // Render mobile card view
  const renderMobileCard = (cr, index) => {
    const isExpanded = expandedCards[cr.classId] || false;

    return (
      <Card
        key={cr.classId}
        sx={{
          mb: 2,
          borderLeft: `4px solid ${selectedStudents.includes(cr.classId) ? '#1976d2' : '#e0e0e0'}`,
          backgroundColor: selectedStudents.includes(cr.classId) ? '#e3f2fd' : 'white'
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Checkbox
              checked={selectedStudents.includes(cr.classId)}
              onChange={() => handleStudentSelect(cr.classId)}
              color="primary"
            />
            <Avatar src={DOC_URL + cr.photo} sx={{ width: 50, height: 50 }} />
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                {toTitleCase(cr.studentName)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {cr.standard} - {cr.section} | Roll: {cr.rollNumber}
              </Typography>
            </Box>
            <MuiIconButton onClick={() => toggleCardExpand(cr.classId)}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </MuiIconButton>
          </Box>

          {/* Basic Info always visible */}
          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
            <Chip
              label={cr.active ? 'Active' : 'Inactive'}
              color={cr.active ? 'success' : 'error'}
              size="small"
            />
            {/* {cr.dueAmount > 0 ? (
              <Chip 
                label={`Due: ${formatCurrency(cr.dueAmount)}`} 
                color="error" 
                size="small"
              />
            ) : (
              <Chip 
                label="Paid" 
                color="success" 
                size="small"
              />
            )} */}
          </Box>
        </CardContent>

        <Collapse in={isExpanded}>
          <Divider />
          <CardContent sx={{ pt: 1, pb: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Father</Typography>
                <Typography variant="body2">{toTitleCase(cr.fatherName)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Mother</Typography>
                <Typography variant="body2">{toTitleCase(cr.motherName)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">DOB</Typography>
                <Typography variant="body2">{formatDate(cr.dob)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Mobile</Typography>
                <Typography variant="body2">{cr.mobile}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Annual Fee</Typography>
                <Typography variant="body2">{formatCurrency(cr.annualfee)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Academic Year</Typography>
                <Typography variant="body2">{cr.academicYear}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Address</Typography>
                <Typography variant="body2">{toTitleCase(cr.address) || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </CardContent>

          <Divider />
          <CardActions sx={{ flexWrap: 'wrap', gap: 0.5, p: 1.5 }}>
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                onClick={() => { setFeesFormView(true); setFeesEntry(cr) }}
                title="Add Fees"
              >
                <CurrencyRupee fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  navigate('/fees-view', {
                    state: {
                      classRoom: cr
                    }
                  })
                }
                title="View Fees Details"
              >
                <FactCheck fontSize="small" />
              </IconButton>
            </Stack>
            {/* Attendance Buttons */}
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                onClick={() => handleAttend(cr, "PRESENT")}
                sx={{
                  border: "1px solid",
                  borderRadius: "8px",
                  fontSize: "10px",
                  fontWeight: 'bold',
                  p: 0.5,
                  borderColor: cr.attendance?.attendance === "PRESENT" ? 'success.main' : 'grey.400',
                  bgcolor: cr.attendance?.attendance === "PRESENT" ? 'success.light' : 'transparent',
                }}
              >
                P
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleAttend(cr, "ABSENT")}
                sx={{
                  border: "1px solid",
                  borderRadius: "8px",
                  fontSize: "10px",
                  fontWeight: 'bold',
                  p: 0.5,
                  borderColor: cr.attendance?.attendance === "ABSENT" ? 'error.main' : 'grey.400',
                  bgcolor: cr.attendance?.attendance === "ABSENT" ? 'error.light' : 'transparent',
                }}
              >
                A
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleAttend(cr, "LEAVE")}
                sx={{
                  border: "1px solid",
                  borderRadius: "8px",
                  fontSize: "10px",
                  fontWeight: 'bold',
                  p: 0.5,
                  borderColor: cr.attendance?.attendance === "LEAVE" ? 'warning.main' : 'grey.400',
                  bgcolor: cr.attendance?.attendance === "LEAVE" ? 'warning.light' : 'transparent',
                }}
              >
                L
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleAttend(cr, "HALF_DAY")}
                sx={{
                  border: "1px solid",
                  borderRadius: "8px",
                  fontSize: "10px",
                  fontWeight: 'bold',
                  p: 0.5,
                  borderColor: cr.attendance?.attendance === "HALF_DAY" ? 'info.main' : 'grey.400',
                  bgcolor: cr.attendance?.attendance === "HALF_DAY" ? 'info.light' : 'transparent',
                }}
              >
                HD
              </IconButton>
            </Stack>

            <IconButton size="small" onClick={() => handleMarkentry(cr)} title="Mark Entries">
              <TableViewSharp fontSize="small" />
            </IconButton>

            {cr.academicYear !== year && (
              <IconButton size="small" onClick={() => handlePromo(cr)} title="Promotion">
                <Add fontSize="small" />
              </IconButton>
            )}

            {roles === "ADMIN" && (
              <IconButton size="small" onClick={() => handleEdit(cr)} title="Update">
                <Edit fontSize="small" />
              </IconButton>
            )}
          </CardActions>
        </Collapse>
      </Card>
    );
  };

  // Render column header cell
  const renderColumnHeader = (columnKey) => {
    const config = columnConfig[columnKey];
    if (!config) return null;

    switch (columnKey) {
      case 'select':
        return (
          <TableCell padding="checkbox" key={columnKey}>
            <Checkbox
              checked={selectAll}
              onChange={handleSelectAll}
              color="primary"
              indeterminate={selectedStudents.length > 0 && selectedStudents.length < classRooms.length}
              disabled={classRooms.length === 0}
              size={isMobile ? "small" : "medium"}
            />
          </TableCell>
        );
      case 'id':
        return <TableCell key={columnKey}><strong>ID</strong></TableCell>;
      case 'photo':
        return <TableCell key={columnKey}><strong>Photo</strong></TableCell>;
      case 'studentName':
        return <TableCell key={columnKey}><strong>Student Name</strong></TableCell>;
      case 'fatherName':
        return <TableCell key={columnKey}><strong>Father</strong></TableCell>;
      case 'motherName':
        return <TableCell key={columnKey}><strong>Mother</strong></TableCell>;
      case 'standard':
        return <TableCell key={columnKey}><strong>Standard</strong></TableCell>;
      case 'section':
        return <TableCell key={columnKey}><strong>Section</strong></TableCell>;
      case 'medium':
        return <TableCell key={columnKey}><strong>Medium</strong></TableCell>;
      case 'academicYear':
        return <TableCell key={columnKey}><strong>Academic Year</strong></TableCell>;
      case 'rollNumber':
        return <TableCell key={columnKey}><strong>Roll No.</strong></TableCell>;
      case 'patternType':
        return <TableCell key={columnKey}><strong>Pattern</strong></TableCell>;
      case 'studentType':
        return <TableCell key={columnKey}><strong>Student Type</strong></TableCell>;
      case 'dob':
        return <TableCell key={columnKey}><strong>DOB</strong></TableCell>;
      case 'mobile':
        return <TableCell key={columnKey}><strong>Mobile</strong></TableCell>;
      case 'address':
        return <TableCell sx={{ minWidth: 120 }}><strong>Address</strong></TableCell>;
      case 'promotionDate':
        return <TableCell key={columnKey}><strong>Promotion Date</strong></TableCell>;
      case 'admissionDate':
        return <TableCell key={columnKey}><strong>Admission Date</strong></TableCell>;
      case 'active':
        return <TableCell key={columnKey}><strong>Status</strong></TableCell>;
      case 'annualfee':
        return <TableCell key={columnKey}><strong>Annual Fee</strong></TableCell>;
      // case 'dueAmount':
      //   return <TableCell key={columnKey}><strong>Due Amount</strong></TableCell>;
      case 'fees':
        return <TableCell key={columnKey}><strong>Fees</strong></TableCell>;
      case 'attendance':
        return <TableCell key={columnKey}><strong>Attendance</strong></TableCell>;
      case 'marks':
        return <TableCell key={columnKey}><strong>Mark Entry</strong></TableCell>;
      case 'actions':
        return roles === "ADMIN" ? <TableCell key={columnKey}><strong>Actions</strong></TableCell> : null;
      default:
        return null;
    }
  };

  // Render column cell
  const renderColumnCell = (columnKey, cr, index) => {
    switch (columnKey) {
      case 'select':
        return (
          <TableCell key={columnKey} padding="checkbox">
            <Checkbox
              checked={selectedStudents.includes(cr.classId)}
              onChange={() => handleStudentSelect(cr.classId)}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </TableCell>
        );
      case 'id':
        return <TableCell key={columnKey}>{cr.classId}</TableCell>;
      case 'photo':
        return (
          <TableCell key={columnKey}>
            <Avatar src={DOC_URL + cr.photo} sx={{ width: 40, height: 40 }} />
          </TableCell>
        );
      case 'studentName':
        return <TableCell key={columnKey}>{toTitleCase(cr.studentName)}</TableCell>;
      case 'fatherName':
        return <TableCell key={columnKey}>{toTitleCase(cr.fatherName)}</TableCell>;
      case 'motherName':
        return <TableCell key={columnKey}>{toTitleCase(cr.motherName)}</TableCell>;
      case 'standard':
        return <TableCell key={columnKey}>{cr.standard}</TableCell>;
      case 'section':
        return <TableCell key={columnKey}>{cr.section}</TableCell>;
      case 'medium':
        return <TableCell key={columnKey}>{cr.medium || 'N/A'}</TableCell>;
      case 'academicYear':
        return <TableCell key={columnKey}>{cr.academicYear}</TableCell>;
      case 'rollNumber':
        return <TableCell key={columnKey}>{cr.rollNumber}</TableCell>;
      case 'patternType':
        return <TableCell key={columnKey}>{cr.patternType || 'N/A'}</TableCell>;
      case 'studentType':
        return <TableCell key={columnKey}>{cr.studentType || 'N/A'}</TableCell>;
      case 'dob':
        return <TableCell key={columnKey}>{formatDate(cr.dob)}</TableCell>;
      case 'mobile':
        return <TableCell key={columnKey}>{cr.mobile}</TableCell>;
      case 'address':
        return <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {toTitleCase(cr.address) || 'N/A'}
        </TableCell>;
      case 'promotionDate':
        return <TableCell key={columnKey}>{formatDate(cr.promotionDate)}</TableCell>;
      case 'admissionDate':
        return <TableCell key={columnKey}>{formatDate(cr.admissionDate)}</TableCell>;
      case 'active':
        return (
          <TableCell key={columnKey}>
            <Chip
              label={cr.active ? 'Active' : 'Inactive'}
              color={cr.active ? 'success' : 'error'}
              size="small"
            />
          </TableCell>
        );
      case 'annualfee':
        return <TableCell key={columnKey}>{formatCurrency(cr.annualfee)} || {formatCurrency(cr.vehicleFee)}</TableCell>;
      // case 'dueAmount':
      //   return (
      //     <TableCell key={columnKey}>
      //       {cr.dueAmount > 0 ? (
      //         <Chip
      //           label={formatCurrency(cr.dueAmount)}
      //           color="error"
      //           size="small"
      //         />
      //       ) : (
      //         <Chip
      //           label="Paid"
      //           color="success"
      //           size="small"
      //         />
      //       )}
      //     </TableCell>
      //   );
      case 'fees':
        return (
          <TableCell key={columnKey}>
            <IconButton
              onClick={() => { setFeesFormView(true); setFeesEntry(cr) }}
              title="Add Fees"
              size={isMobile ? "small" : "medium"}
            >
              <CurrencyRupee sx={{ border: "1px solid", borderRadius: "10px" }} />
            </IconButton>
            {roles === "ADMIN" && (
              <IconButton
                onClick={() =>
                  navigate('/fees-view', {
                    state: {
                      classRoom: cr
                    }
                  })
                }
                title="View Fees Details"
                size={isMobile ? "small" : "medium"}
              >
                <FactCheck sx={{ border: "1px solid", borderRadius: "10px" }} />
              </IconButton>
            )}
          </TableCell>
        );
      case 'attendance':
        return (
          <TableCell key={columnKey}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {['PRESENT', 'ABSENT', 'LEAVE', 'HALF_DAY'].map((status) => {
                const label = status === 'HALF_DAY' ? 'HD' : status.charAt(0);
                const colorMap = {
                  'PRESENT': { main: 'success', light: 'success.light' },
                  'ABSENT': { main: 'error', light: 'error.light' },
                  'LEAVE': { main: 'warning', light: 'warning.light' },
                  'HALF_DAY': { main: 'info', light: 'info.light' }
                };
                const isActive = cr.attendance?.attendance === status;
                return (
                  <IconButton
                    key={status}
                    size="small"
                    onClick={() => handleAttend(cr, status)}
                    title={status}
                    sx={{
                      border: "1px solid",
                      borderRadius: "8px",
                      fontSize: isMobile ? "8px" : "10px",
                      fontWeight: 'bold',
                      p: isMobile ? 0.3 : 0.5,
                      minWidth: isMobile ? 20 : 28,
                      minHeight: isMobile ? 20 : 28,
                      borderColor: isActive ? colorMap[status].main + '.main' : 'grey.400',
                      bgcolor: isActive ? colorMap[status].light : 'transparent',
                    }}
                  >
                    {label}
                  </IconButton>
                );
              })}
            </Stack>
          </TableCell>
        );
      case 'marks':
        return (
          <TableCell key={columnKey}>
            <Stack direction="row" spacing={0.5}>
              {cr.academicYear !== year && (
                <IconButton
                  onClick={() => handlePromo(cr)}
                  title="Promotion"
                  size={isMobile ? "small" : "medium"}
                >
                  <Add fontSize={isMobile ? "small" : "medium"} sx={{ border: "1px solid", borderRadius: "10px" }} />
                </IconButton>
              )}
              <IconButton
                title="Mark Entries"
                onClick={() => handleMarkentry(cr)}
                size={isMobile ? "small" : "medium"}
              >
                <TableViewSharp fontSize={isMobile ? "small" : "medium"} sx={{ border: "1px solid", borderRadius: "10px" }} />
              </IconButton>
            </Stack>
          </TableCell>
        );
      case 'actions':
        return roles === "ADMIN" ? (
          <TableCell key={columnKey}>
            <IconButton
              onClick={() => handleEdit(cr)}
              title="Update"
              size={isMobile ? "small" : "medium"}
            >
              <Edit fontSize={isMobile ? "small" : "medium"} sx={{ border: "2px solid", borderRadius: "10px" }} />
            </IconButton>
          </TableCell>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div sx={{ p: isMobile ? 1 : 2 }}>
      <Box elevation={3} sx={{ p: isMobile ? 1 : 2, overflow: 'hidden' }}>
        {/* ---------- HEADER ---------- */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          px={isMobile ? 1 : 2}
          py={1}
        >
          <Typography variant={isMobile ? "subtitle1" : "h5"} fontWeight="bold" color="primary">
            <SupervisedUserCircle sx={{ mr: 1, mb: -0.5 }} />
            Class Room
          </Typography>

          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
            <Tooltip title="Customize Columns">
              <IconButton
                color="warning"
                onClick={handleColumnPopoverOpen}
                size={isMobile ? "small" : "medium"}
              >
                <ViewColumnIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Send pending fees message">
              <IconButton
                color="warning"
                onClick={handleOpenMessageDialog}
                size={isMobile ? "small" : "medium"}
              >
                <MessageIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Refresh">
              <IconButton
                color="warning"
                onClick={() => handleRefreshSection()}
                size={isMobile ? "small" : "medium"}
              >
                <Refresh fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Create Bulk Entry Cards">
              <IconButton
                color="warning"
                onClick={() => { setOpenEntryCard(true); handleOpenEntrycard(); }}
                size={isMobile ? "small" : "medium"}
              >
                <AddCard fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Create Bulk ID Cards">
              <IconButton
                color="warning"
                onClick={handleOpenIDcard}
                size={isMobile ? "small" : "medium"}
              >
                <BadgeIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Column Customization Popover */}
        <Popover
          open={openColumnPopover}
          anchorEl={anchorEl}
          onClose={handleColumnPopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: isMobile ? 280 : 350,
              maxHeight: 500,
              p: 2,
            }
          }}
        >
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontSize="1rem" fontWeight="bold">
                Customize Columns
              </Typography>
              <Button size="small" onClick={handleResetColumns}>
                Reset
              </Button>
            </Box>

            <Divider />

            <List sx={{ pt: 1, overflow: 'auto', maxHeight: 350 }}>
              {columnOrder.map((colKey) => {
                const config = columnConfig[colKey];
                if (!config) return null;
                const isVisible = columnVisibility[colKey] !== false;
                const isSelectColumn = colKey === 'select';

                return (
                  <ListItem
                    key={colKey}
                    dense
                    sx={{
                      borderRadius: 1,
                      '&:hover': { backgroundColor: 'action.hover' },
                      opacity: isSelectColumn ? 0.7 : 1,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {config.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={config.label}
                      secondary={isSelectColumn ? "Required" : ""}
                      primaryTypographyProps={{ variant: isMobile ? 'caption' : 'body2' }}
                    />
                    <Switch
                      edge="end"
                      checked={isVisible}
                      onChange={() => handleColumnToggle(colKey)}
                      disabled={isSelectColumn}
                      color="primary"
                      size="small"
                    />
                    {isVisible ? (
                      <VisibilityIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                    ) : (
                      <VisibilityOffIcon fontSize="small" color="disabled" sx={{ ml: 1 }} />
                    )}
                  </ListItem>
                );
              })}
            </List>

            <Divider sx={{ my: 1 }} />

            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center' }}>
              {getVisibleColumns().length - 1} of {Object.keys(columnConfig).length - 1} columns visible
            </Typography>
          </Box>
        </Popover>

        {/* ---------- FILTER ---------- */}
        <Box px={isMobile ? 1 : 2} pb={2} display="flex" gap={1} flexDirection={isMobile ? "column" : "row"}>
          <TextField
            select
            fullWidth
            label="Select standard/class"
            value={selectedStandard || ""}
            onChange={handleChange}
            size={isMobile ? "small" : "medium"}
          >
            {standards?.map((s, i) => (
              <MenuItem key={i} value={s}>{s}</MenuItem>
            ))}
          </TextField>

          {selectedStudents.length > 0 && (
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="body2" color="primary">
                {selectedStudents.length} selected
              </Typography>
              <Button
                variant="contained"
                color="warning"
                size="small"
                startIcon={<SendIcon />}
                onClick={handleOpenMessageDialog}
              >
                Send
              </Button>
            </Box>
          )}
        </Box>

        <Divider />

        {/* ---------- TABLE OR CARD VIEW ---------- */}
        {isMobile ? (
          // Mobile Card View
          <Box sx={{ p: 1 }}>
            {Array.isArray(classRooms) && classRooms?.map((cr, index) => renderMobileCard(cr, index))}
          </Box>
        ) : (
          // Desktop Table View
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size={isTablet ? "small" : "medium"} stickyHeader>
              <TableHead>
                <TableRow>
                  {getVisibleColumns().map((colKey) => renderColumnHeader(colKey))}
                </TableRow>
              </TableHead>

              <TableBody>
                {Array.isArray(classRooms) && classRooms?.map((cr, index) => (
                  <TableRow
                    key={index}
                    selected={selectedStudents.includes(cr.classId)}
                    hover
                  >
                    {getVisibleColumns().map((colKey) => renderColumnCell(colKey, cr, index))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Message Dialog */}
      <Dialog
        open={openMessageDialog}
        onClose={() => !sendingMessage && setOpenMessageDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <MessageIcon color="warning" />
            <Typography variant="h6">Send Pending Fees Message</Typography>
          </Box>
          <Typography variant="body2" color="textSecondary">
            Sending to {selectedStudents.length} selected student(s)
          </Typography>
        </DialogTitle>
        <DialogContent>
          {messageError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessageError("")}>
              {messageError}
            </Alert>
          )}
          {messageSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessageSuccess("")}>
              {messageSuccess}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="template-select-label">
              <Box display="flex" alignItems="center" gap={1}>
                <DescriptionIcon fontSize="small" />
                Select Message Template
              </Box>
            </InputLabel>
            <Select
              labelId="template-select-label"
              value={selectedTemplate}
              onChange={handleTemplateChange}
              label="Select Message Template"
              disabled={sendingMessage}
              size={isMobile ? "small" : "medium"}
            >
              {templateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {messageTemplates[option.value].substring(0, 60)}...
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Template Preview:
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {messageText}
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="Message Preview (Edit if needed)"
            multiline
            rows={isMobile ? 4 : 6}
            value={messageText}
            onChange={handleMessageEdit}
            placeholder="Enter message to send to parents..."
            variant="outlined"
            sx={{ mt: 2 }}
            disabled={sendingMessage}
            helperText={`${messageText.length}/500 characters`}
            inputProps={{ maxLength: 500 }}
            size={isMobile ? "small" : "medium"}
          />

          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              <strong>Selected Students ({selectedStudents.length}):</strong>
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {classRooms
                .filter(cr => selectedStudents.includes(cr.classId))
                .slice(0, isMobile ? 5 : 10)
                .map(cr => (
                  <Chip
                    key={cr.classId}
                    label={`${cr.studentName} (${cr.standard}-${cr.section})`}
                    size="small"
                    onDelete={() => !sendingMessage && handleStudentSelect(cr.classId)}
                  />
                ))}
              {selectedStudents.length > (isMobile ? 5 : 10) && (
                <Chip
                  label={`+${selectedStudents.length - (isMobile ? 5 : 10)} more`}
                  size="small"
                  color="warning"
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexWrap: 'wrap' }}>
          <Button
            onClick={() => setOpenMessageDialog(false)}
            disabled={sendingMessage}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSendMessage}
            disabled={sendingMessage || !messageText.trim()}
            startIcon={sendingMessage ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingMessage ? "Sending..." : "Send Message"}
          </Button>
        </DialogActions>
      </Dialog>

      <ModalView
        open={openForm}
        handleClose={handleClose}
        title={editingClassRoom ? "Update Class Room" : "Promote Student"}
        content={<ClassRoomForm initialData={editingClassRoom ? editingClassRoom : classRoom} onSubmit={handleAdd} />}
      />

      <ModalView size={isMobile ? 'sm' : 'sm'}
        open={feesForm}
        handleClose={handleClose}
        title="Fees Entry"
        content={<FeesentryForm
          classRoom={feesEntry}
          masterId={user?.mas_id}
          onSuccess={() => {
            setFeesForm(false);
            setFeesFormView(false);
          }}
        />}
      />

      <ModalView size={isMobile ? 'sm' : 'sm'}
        open={feesFormView}
        handleClose={handleClose}
        title="Fees Details"
        content={<FeesentryView
          classRoom={feesEntry}
          masterId={user?.mas_id}
          handleCloseFee={handleCloseFee}
          onSuccess={() => {
            setFeesFormView(false);
            setFeesForm(false)
          }}

        />}
      />

      <ModalView size={isMobile ? 'md' : 'lg'}
        open={openEntry}
        handleClose={handleClose}
        title="Add Marks"
        content={<MarksheetForm classRoomid={markEntry.classId} onSuccess={() => {
          setOpenEntry(false);
        }} />}
      />

      <ModalView size={isMobile ? 'sm' : 'md'}
        open={openEntryCard}
        handleClose={handleClose}
        title="Add Time Table"
        content={<EntryCardForm entryCard={entryCard} onSuccess={() => {
          setOpenEntryCard(false);
        }} />}
      />
    </div>
  );
}
export default ClassRoomPage;